import { ValidationErrors, Validators } from '@angular/forms';

import {
	catchError,
	debounceTime,
	filter,
	finalize,
	first,
	map,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs/operators';

import {
	Observable,
	Observer,
	merge,
	of,
	throwError,
} from 'rxjs';

import { ApplicationException } from 'ib/internetbank-core/ib-exceptions/ib-exceptions';
import { Amount } from 'ib/internetbank-core/ib-models/amount';
import { DateUtils, ISODateString } from 'ib/internetbank-core/utils/date-utils';


import { CustomValidators } from '../../../../common/custom-validation/custom-validators';
import { AMOUNT_ASYNC_VALIDATOR_DEFAULT_MESSAGES } from '../../../../common/custom-validation/operation-amount-async.validator';
import { OperationSignDocumentRequiredException } from '../../../../common/exceptions/exceptions';
import { AmountInCurrencyPipe } from '../../../../common/pipes/amount-in-currency.pipe';
import { WizardDataStep } from '../../../../shared/wizard-v2/wizard-core/steps/base-step-types/wizard-data-step';
import { FieldOptions } from '../../../../ui/controls/field.model';
import { ListFieldsComponent } from '../../../../ui/controls/list-fields/list-fields.component';
import { ProgressLabelFieldComponent } from '../../../../ui/controls/progress-label-field/progress-label-field.component';
import { SelectAccountFieldOptions } from '../../../../ui/controls/select-account-field/models/select-account-field-options';
import { CustomFormControl } from '../../../../ui/custom-reactive-forms/custom-form-control';
import { CustomFormGroup } from '../../../../ui/custom-reactive-forms/custom-form-group';
import { FormAccessorUtils, SafetyFormAccessor } from '../../../../ui/form/form.model';
import { InsuranceIdByTypes } from '../../insurance';
import { InsuranceSportCreateWizardComponent } from '../insurance-sport-create-wizard.component';
import {
	InsuranceSportAutocompletePersonalData,
	InsuranceSportCreateData,
	InsuranceSportPersonalInfo,
	MAX_LIMIT_INSURED,
} from '../insurance-sport-create-wizard.model';

import { InsuranceSportCreateWizardFirstStepInsuranceParams } from './insurance-sport-create-wizard-first-step-insurance-params';


export class InsuranceSportCreateWizardFirstStepDataOfInsured extends WizardDataStep<InsuranceSportCreateWizardComponent, InsuranceSportAutocompletePersonalData> {

	public formAccessor: SafetyFormAccessor;

	public onInit(clientInfo: InsuranceSportAutocompletePersonalData): Observable<void> {
		this.formAccessor = {
			...(this.wizard.steps[this.wizard.stepIndex! - 1] as InsuranceSportCreateWizardFirstStepInsuranceParams).formAccessor,
			insured: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'insured'),
			premiumSum: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'premiumSum'),
			email: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'email'),
			isSportInsuranceDocumentAgree: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'isSportInsuranceDocumentAgree'),
			source: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'source'),
			transferFee: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'transferFee'),
		};

		return this.createInitialForm(clientInfo)
			.pipe(
				tap(() => {
					this.form?.enable({ emitEvent: false });

					window.scrollTo(0, 0);
				}),
			);
	}

	public onExecute(): Observable<void> {
		return this.wizardComponent.insuranceSportCreateWizardService.create(
			this.getCreateBody(),
		).pipe(
			tap(() => this.formAccessor.transferFee().fieldOptions.visible = true),
			catchError((error: ApplicationException) => {

				if (error instanceof OperationSignDocumentRequiredException) {

					this.wizardComponent.operationSignDocumentRequired = error.operationSignDocumentRequired;

					if (error.operationSignDocumentRequired.documentId) {
						return this.initSignDocument(error.operationSignDocumentRequired.operationId, error.operationSignDocumentRequired.documentId)
							.pipe(
								switchMap(() => this.wizardComponent.documentSignWizardService.signDocumentRegister({
									documentId: error.operationSignDocumentRequired.documentId,
									createActionId: error.operationSignDocumentRequired.operationId,
								})),
							);
					}
				}

				return throwError(() => error);
			}),
		);
	}

	public override  prepareBack(): Observable<void> {
		return super.prepareBack(false)
			.pipe(
				tap(() => {
					(this.form?.getControlFromAllLevels('main') as unknown as CustomFormGroup)
						.formGroupWrapper.next(undefined);
					this.form?.removeControl('pdfIframeControl');

					this.formAccessor.insured().updateValueAndValidity({ emitEvent: false });
					this.formAccessor.transferFee().fieldOptions.visible = false;
				}),
			);
	}

	private getCreateBody(): InsuranceSportCreateData {

		const insuredPersons = this.getPersonalInfoArray();
		const { numberOfAdults, numberOfChildren } = this.getNumberOfInsuredByAge(insuredPersons);

		return {
			insuranceId: InsuranceIdByTypes.SPORT,
			numberOfAdults,
			numberOfChildren,
			insuredPersons,
			insuranceSum: this.formAccessor.insuranceSum().value,
			premiumSum: this.getPremiumSumNumber(),
			email: this.formAccessor.email().value,
			isSportInsuranceDocumentAgree: this.formAccessor.isSportInsuranceDocumentAgree().value,
			contractId: this.formAccessor.source().value.productId.contractId,
			sportTypeIds: this.formAccessor.sportTypes().value,
			annual: this.formAccessor.annual().value,
			beginDate: this.formAccessor.policyValidityPeriod().value[0],
			endDate: this.formAccessor.policyValidityPeriod().value[1],
			durationId: this.formAccessor.duration().value,
		};
	}

	private initSignDocument(actionId: number, documentId: number): Observable<void> {
		return this.wizardComponent.documentSignWizardService.getDocumentPdf(documentId)
			.pipe(
				tap((value: Blob) => {
					(this.form?.getControlFromAllLevels('main') as unknown as CustomFormGroup)
						.formGroupWrapper.next('hidden-block');

					this.form?.addCustomControlOnFieldOptions(
						new FieldOptions({
							'@type': 'pdf-iframe-control',
							name: 'pdfIframeControl',
							value,
						}),
					);
				}),
				switchMap(() => this.wizardComponent.documentSignWizardService.signDocumentRegister(
					{
						documentId: documentId,
						createActionId: actionId,
					})),
			);
	}

	private getPersonalInfoArray(): Array<InsuranceSportPersonalInfo> {
		return this.formAccessor.insured().fieldOptions.items
			?.map((item: CustomFormGroup) => ({
				surname: item.value.surname,
				name: item.value.name,
				patronymic: item.value.patronymic || '-',
				birthdate: item.value.birthdate[0],
			})) || [];
	}

	private createInitialForm(clientInfo: InsuranceSportAutocompletePersonalData): Observable<void> {
		return new Observable((observer: Observer<void>) => {
			this.stepInformation.setSubmitLabel('Оформить');

			this.form = new CustomFormGroup({
				main: new CustomFormGroup({
					text: this.formAccessor.text(),
					insured: new CustomFormControl(
						new FieldOptions({
							'@type': 'list-fields',
							name: 'insured',
							addItemLabel: 'Добавить застрахованное лицо',
							itemsCount: MAX_LIMIT_INSURED,
							isButtonAlwaysVisible: true,
							disabled: false,
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							newItemConstructor: (value: any, fieldOptions: FieldOptions): CustomFormControl | CustomFormGroup | undefined => this.addInsuredTemplate(fieldOptions.items?.length || 0),
							onTemplateControlInitCallback: (controlComponent: ListFieldsComponent) => {
								const insuredCount: number = this.formAccessor.numberOfAdultsInput().value + this.formAccessor.numberOfChildrenInput().value;

								for (let i = 0; i < insuredCount; i++) {
									controlComponent.add();
								}

								if (this.formAccessor.checkoutForYourself().value) {
									const firstInsuredGroup: CustomFormGroup = controlComponent.fieldOptions.items?.[0];
									firstInsuredGroup.patchValue(clientInfo);
								}
							},
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							onDelete: (value: any, customFormGroup: CustomFormGroup, controlComponent: ListFieldsComponent) => {
								this.wizardComponent.checkRemoveInsuredPopupId.open();
								return this.wizardComponent.isRemovedInsured
									.pipe(
										first(),
										tap(() => {
											let index: number;
											controlComponent.fieldOptions.items
												?.forEach((item: CustomFormGroup, i: number) => {
													if (item === customFormGroup) {
														index = i;
													}
													if (i > index) {
														item.controls.groupTitle.setValue(`${i}-е застрахованное лицо`);
													}
												});
										}),
										finalize(() => {
											this.calculatePremiumSum();
											this.formAccessor.insured().updateValueAndValidity({ emitEvent: false });
										}),
									);
							},
						}),
						undefined,
						(control: CustomFormControl): Observable<ValidationErrors | null> => {
							if (control.fieldOptions.items?.length) {
								return merge(
									of(control.fieldOptions.items),
									...control.fieldOptions.items.map((formGroup: CustomFormGroup) => formGroup.valueChanges),
								)
									.pipe(
										map(() => control.fieldOptions.items?.every((formGroup: CustomFormGroup) => formGroup.valid)
											? null : { invalidFormGroup: true }),
									);
							}
							return of({ invalidFormGroup: true });
						},
					),
					premiumSum: new CustomFormControl(
						new FieldOptions({
							'@type': 'progress-label-field',
							name: 'premiumSum',
							label: 'Стоимость полиса',
							value: '0.00 ₽',
						}),
					),
					email: new CustomFormControl(
						new FieldOptions({
							'@type': 'text-field',
							name: 'email',
							label: 'Email страхователя',
							required: true,
							value: clientInfo.email || null,
						}),
						[CustomValidators.email()],
					),
					isSportInsuranceDocumentAgree: new CustomFormControl(
						new FieldOptions({
							'@type': 'checkbox-field',
							name: 'isSportInsuranceDocumentAgree',
							label: (() => {
								let link = 'Ключевым информационным документом';
								const href = this.wizard.getStepByType(
									InsuranceSportCreateWizardFirstStepInsuranceParams,
								).insuranceSportFormData.sportInsuranceInfoDocumentLink;
								if (href) {
									link = `<a class="link" target="_blank" href="${href}">${link}</a>`
								}
								return `Я&nbsp;ознакомлен с&nbsp;${link} и&nbsp;хочу заключить договор страхования`;
							})(),
						}),
						[Validators.requiredTrue],
					),
					source: new CustomFormControl(
						new SelectAccountFieldOptions({
							'@type': 'select-account-field',
							name: 'source',
							label: 'Со счета',
							hints: { cursorHint: 'Выберите счет-источник' },
							css: { classList: ['select-element'] },
							showCardIcon: true,
							required: true,
							value: undefined,
							dataSource$: this.wizardComponent.insuranceSportCreateWizardService.getSourceList(),
						}),
					),
					transferFee: new CustomFormControl(
						new FieldOptions({
							'@type': 'label-field',
							name: 'premiumSum',
							label: 'Плата за перевод',
							value: '0.00 ₽',
							visible: false,
						}),
					),
				},
					(): ValidationErrors | null => {
						if (this.form) {
							if ((this.formAccessor.source().value?.balanceInfo?.amount - this.getPremiumSumNumber()) < 0) {
								return CustomValidators.createValidationError(
									AMOUNT_ASYNC_VALIDATOR_DEFAULT_MESSAGES.invalidBalance,
								);
							}

							if ((this.formAccessor.premiumSum().templateComponentRef as ProgressLabelFieldComponent)?.loading) {
								return { isPremiumSumLoaded: false };
							}
						}

						return null;
					},
				),
			});

			observer.next();
			observer.complete();

		});
	}

	private addInsuredTemplate(index: number): CustomFormGroup {
		const formGroup = new CustomFormGroup({
			groupTitle: new CustomFormControl(
				new FieldOptions({
					'@type': 'group-title-field',
					name: 'insuredGroupTitle',
					value: `${index + 1}-е застрахованное лицо`,
				}),
			),
			surname: new CustomFormControl(
				new FieldOptions({
					'@type': 'text-field',
					name: 'surname',
					label: 'Фамилия',
					inputMask: { regex: '[а-яА-ЯёЁ-]{0,70}' },
					required: true,
				}),
			),
			name: new CustomFormControl(
				new FieldOptions({
					'@type': 'text-field',
					name: 'name',
					label: 'Имя',
					inputMask: { regex: '[а-яА-ЯёЁ-]{0,50}' },
					required: true,
				}),
			),
			patronymic: new CustomFormControl(
				new FieldOptions({
					'@type': 'text-field',
					name: 'patronymic',
					label: 'Отчество',
					inputMask: { regex: '[а-яА-ЯёЁ-]{0,50}' },
				}),
			),
			birthdate: new CustomFormControl(
				new FieldOptions({
					'@type': 'date-picker-input',
					label: 'Дата рождения',
					name: 'birthdate',
					itemsCount: 1,
					maxDate: new Date(new Date().setDate(new Date().getDate() - 1)),
					required: true,
					hints: { underLineHint: 'от 2-х до 65 лет' },
				}),
				[
					CustomValidators.regularDatesPeriod(
						undefined,
						new Date(new Date().setDate(new Date().getDate() - 1)),
						{ datesCount: 1 },
					),
				],
			),
		});

		const birthdateControl: CustomFormControl | null = formGroup.getControlFromAllLevels('birthdate');

		this.triggerControlForСalculatePremiumSum(birthdateControl);

		setTimeout(() => {
			this.formAccessor.insured().updateValueAndValidity({ emitEvent: false });
		});

		return formGroup;
	}

	private triggerControlForСalculatePremiumSum(birthdateControl: CustomFormControl | null): void {
		birthdateControl?.valueChanges
			.pipe(
				debounceTime(this.wizardComponent.localDebounceTime),
				tap(() => birthdateControl.markAsFailureEnabled()),
				filter((v: unknown) => !!v),
				takeUntil(this.wizardComponent.ngUnsubscribe$$),
			)
			.subscribe(() => {
				this.calculatePremiumSum();
			});
	}

	private getBodyForPremiumSumProductInsurance() {
		const birthdateValues = this.formAccessor.insured().fieldOptions.items?.map((item: CustomFormGroup) =>
			item.controls.birthdate.value,
		).filter((item: [ISODateString]) => !!item)
			.map((item: [ISODateString]) => item[0]);

		return {
			sportTypeIds: this.formAccessor.sportTypes().value,
			insuranceSum: this.formAccessor.insuranceSum().value,
			annual: this.formAccessor.annual().value,
			beginDate: this.formAccessor.policyValidityPeriod().value[0],
			endDate: this.formAccessor.policyValidityPeriod().value[1],
			durationId: this.formAccessor.duration().value,
			birthDates: birthdateValues,
		};
	}

	private calculatePremiumSum(): void {
		(this.formAccessor.premiumSum().templateComponentRef as ProgressLabelFieldComponent).loading = true;
		this.form?.updateValueAndValidity({ emitEvent: false });


		this.wizardComponent.insuranceSportCreateWizardService.getPremiumSum(
			this.getBodyForPremiumSumProductInsurance(),
		)
			.subscribe((value: Amount) => {
				this.formAccessor.premiumSum().setValue(new AmountInCurrencyPipe().transform(value), { emitEvent: false });
				(this.formAccessor.premiumSum().templateComponentRef as ProgressLabelFieldComponent).loading = false;
				this.form?.updateValueAndValidity({ emitEvent: false });

			});
	}

	private getPremiumSumNumber(): number {
		return +this.formAccessor.premiumSum()?.value?.replace(/ .$/, '')?.replace(/\s/gi, '');
	}

	private getNumberOfInsuredByAge(insuredPersons: Array<InsuranceSportPersonalInfo>): {
		numberOfAdults: number;
		numberOfChildren: number
	} {
		let numberOfAdults = 0;
		let numberOfChildren = 0;

		insuredPersons.forEach((item: InsuranceSportPersonalInfo) => {
			const birthdayTime = DateUtils.getBeginningOfToday(new Date(item.birthdate)).getTime();
			const nowTime = DateUtils.getBeginningOfToday(new Date()).getTime();
			const age = (nowTime - birthdayTime) / (1000 * 60 * 60 * 24) / 365.25;

			if (age >= 18) {
				numberOfAdults++;
			} else {
				numberOfChildren++;
			}
		});

		return {
			numberOfAdults,
			numberOfChildren,
		};
	}
}
